import { type BaseApiResponse, dangerousAxiosInstanceUseOnlyIfNeeded } from '@lumapps/base-api';
import { getUserMsToken } from '@lumapps/user/api';
import type { FileElement } from '@lumapps/wrex-file/types';

import type { OneDriveItemFacet } from '../constants';
import type { OneDriveDocument, OneDriveFileIdentifier, SharePointSite } from '../types';

/**
 * Sharepoint graph APIs base endpoint.
 * */
export const BASE_URL = 'https://graph.microsoft.com/v1.0';

/**
 * Parameters for batching OneDrive file requests.
 */
export interface BatchOneDriveFilesRequestParam {
    isMicrosoftUser?: boolean;
    token?: string;
    files?: OneDriveFileIdentifier[];
    /**
     * Original files to return when an error occurred on one of the batch requests
     * */
    backupFiles?: FileElement[];
}

/**
 * Parameters for fetching OneDrive folder content.
 */
export interface OneDriveFolderContentRequestParam {
    /** The drive's ID where the folder is located. */
    driveId?: string;
    /** Whether the connected user is a microsoft user. */
    isMicrosoftUser?: boolean;
    /** The folder's ID. */
    itemId?: string;
    /** List params to send to the API. */
    listParams?: {
        /** The number of items to return in a page. */
        top?: number;
    };
    /** The link to the next page of results if it exists. */
    nextPageLink?: string;
    /** The search query. */
    q?: string;
}

/**
 * Response for a single request within a batch request.
 */
export interface BatchOneDriveFilesRequestResponse {
    id: string;
    status: number;
    headers: Record<string, string>;
    body: OneDriveDocument | { error: any };
}

/**
 * Response for a folder content request.
 */
export interface OneDriveFolderContentResponse {
    value: OneDriveDocument[];
    '@odata.nextLink'?: string;
}

/**
 * Parameters for getting the Microsoft SharePoint site.
 */
export interface GetMsSiteParams {
    isMicrosoftUser?: boolean;
}

/**
 * Try to access the DriveItem from a shared URL
 * @param {string} requestToken - User microsoft graph access token
 * @param {string} encodedUrl - Encoded shared URL (for encoding rules see : https://learn.microsoft.com/en-us/graph/api/shares-get?view=graph-rest-1.0&tabs=http#encoding-sharing-urls)
 * */
export const getOneDriveItemFromSharedURL = async (requestToken: string, encodedUrl: string) =>
    dangerousAxiosInstanceUseOnlyIfNeeded.get<OneDriveDocument>(`${BASE_URL}/shares/${encodedUrl}/driveItem`, {
        headers: {
            Authorization: `Bearer ${requestToken}`,
        },
    });

/**
 * Get the provided one drive files
 * Limited to 20 files
 * cf: https://learn.microsoft.com/en-us/graph/json-batching
 */
export const getBatchOneDriveFilesApi = async (
    params?: BatchOneDriveFilesRequestParam,
    signal?: AbortSignal,
): Promise<OneDriveDocument[]> => {
    if (!params || !params.files || !params.isMicrosoftUser) {
        return [];
    }

    const { files } = params;

    const requestPayload = {
        requests: files.map((file, index) => ({
            id: index,
            method: 'GET',
            url: `/drives/${file.driveId}/items/${file.id}?$expand=thumbnails`,
        })),
    };

    const { data: tokenData } = await getUserMsToken();

    if (!tokenData) {
        return [];
    }

    const { data }: BaseApiResponse<{ responses: BatchOneDriveFilesRequestResponse[] }> =
        await dangerousAxiosInstanceUseOnlyIfNeeded.post(`${BASE_URL}/$batch`, requestPayload, {
            signal,
            headers: {
                Authorization: `Bearer ${tokenData.accessToken}`,
            },
        });
    return data.responses.map((res) => {
        const responseBody = res.body as any;
        // Pass the file to the response to know exactly which of the given files had an issue
        if (responseBody.error) {
            return {
                ...responseBody,
                error: {
                    ...responseBody.error,
                    backupFile: params.backupFiles?.[Number(res.id)] || files[Number(res.id)],
                },
            };
        }
        return responseBody as OneDriveDocument;
    });
};

/**
 * Get the content of a folder in OneDrive.
 * @param params The request parameters.
 * @param signal The abort signal.
 * @returns The folder content response or null if not a Microsoft user or no token.
 */
export const getOneDriveFolderContentApi = async (
    params?: OneDriveFolderContentRequestParam,
    signal?: AbortSignal,
): Promise<OneDriveFolderContentResponse | null> => {
    if (!params || !params.isMicrosoftUser) {
        return null;
    }
    const { driveId, itemId, listParams, nextPageLink, q } = params;
    const { data: tokenData } = await getUserMsToken();

    if (!tokenData) {
        return null;
    }

    let url = nextPageLink;

    if (!url) {
        const searchPath = q ? `/search(q='${q}')` : '/children';
        const queryParams = new URLSearchParams();

        queryParams.set('$expand', 'thumbnails');

        if (listParams?.top) {
            queryParams.set('$top', String(listParams.top));
        }
        const queryString = queryParams.toString();

        url = `${BASE_URL}/drives/${driveId}/items/${itemId}${searchPath}${queryString ? `?${queryString}` : ''}`;
    }

    const { data } = await dangerousAxiosInstanceUseOnlyIfNeeded.get<OneDriveFolderContentResponse>(url, {
        signal,
        headers: {
            Authorization: `Bearer ${tokenData.accessToken}`,
        },
    });

    return data;
};

/**
 * Get the root SharePoint site for the current user.
 * @param params The request parameters.
 * @returns The web URL of the root site, or null.
 */
export const getMsSite = async (params?: GetMsSiteParams): Promise<string | null> => {
    if (!params?.isMicrosoftUser) {
        return null;
    }

    const { data: tokenData } = await getUserMsToken();

    if (tokenData) {
        const { data } = await dangerousAxiosInstanceUseOnlyIfNeeded.get<SharePointSite>(`${BASE_URL}/sites/root`, {
            headers: {
                Authorization: `Bearer ${tokenData.accessToken}`,
            },
        });

        return data.webUrl;
    }

    return null;
};

/**
 * Parameters for searching OneDrive files by name.
 */
export interface SearchOneDriveFilesRequestParam {
    /** The search term to find files by name or keyword. */
    query: string;
    /** Maximum number of results to return. Defaults to 10. */
    maxResults?: number;
    /**
     * OneDrive DriveItem facet to filter results server-side via `$filter=<facet> ne null`.
     * When omitted, all file types are returned.
     * Supported: `'image'`, `'video'`, `'audio'`. Others (e.g. `'application'`) have no facet equivalent.
     */
    oneDriveFacet?: OneDriveItemFacet;
}

/** Searches for files across the user's entire OneDrive by name or keyword. */
export const searchOneDriveFilesApi = async (
    params: SearchOneDriveFilesRequestParam,
    signal?: AbortSignal,
): Promise<OneDriveDocument[]> => {
    const { query, maxResults = 10, oneDriveFacet } = params;

    const { data: tokenData } = await getUserMsToken();

    if (!tokenData) {
        return [];
    }

    const queryParams = new URLSearchParams();
    queryParams.set('$top', String(maxResults));
    if (oneDriveFacet) {
        queryParams.set('$filter', `${oneDriveFacet} ne null`);
    }

    const url = `${BASE_URL}/me/drive/root/search(q='${encodeURIComponent(query)}')?${queryParams.toString()}`;

    const { data } = await dangerousAxiosInstanceUseOnlyIfNeeded.get<OneDriveFolderContentResponse>(url, {
        signal,
        headers: {
            Authorization: `Bearer ${tokenData.accessToken}`,
        },
    });

    return (data.value || []).filter((doc) => Boolean(doc.file));
};

/**
 * React Query keys for OneDrive APIs.
 */
export const oneDriveQueryKeys = {
    all: () => ['one-drive'] as const,
    'batch-files': (params?: BatchOneDriveFilesRequestParam) =>
        [...oneDriveQueryKeys.all(), 'batch-files', params] as const,
    'folder-content': (params?: OneDriveFolderContentRequestParam) =>
        [...oneDriveQueryKeys.all(), 'folder-content', params] as const,
    'ms-site': (params?: GetMsSiteParams) => [...oneDriveQueryKeys.all(), 'ms-site', params] as const,
    'search-files': (params?: SearchOneDriveFilesRequestParam) =>
        [...oneDriveQueryKeys.all(), 'search-files', params] as const,
};
